import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../App.jsx';
import { APP_VERSION } from '../version.js';

export default function Login() {
  const [mode, setMode] = useState('login');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const formRef = useRef(null);

  const [username, setUsername] = useState('');
  const [pin, setPin] = useState('');

  const [regUsername, setRegUsername] = useState('');
  const [regPin, setRegPin] = useState('');
  const [regPinConfirm, setRegPinConfirm] = useState('');

  useEffect(() => {
    const handleFocus = () => {
      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    };
    const inputs = document.querySelectorAll('.login-card input');
    inputs.forEach(input => input.addEventListener('focus', handleFocus));
    return () => inputs.forEach(input => input.removeEventListener('focus', handleFocus));
  }, [mode]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login({ username: username.trim(), pin });
      navigate('/');
    } catch (err) {
      setError(err.message || 'Erreur de connexion');
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (regPin !== regPinConfirm) {
      setError('Les codes PIN ne correspondent pas');
      return;
    }
    if (regPin.length !== 6 || !/^\d{6}$/.test(regPin)) {
      setError('Le PIN doit contenir exactement 6 chiffres');
      return;
    }
    setLoading(true);
    try {
      await register({
        username: regUsername.trim(),
        display_name: regUsername.trim(),
        pin: regPin,
      });
      navigate('/');
    } catch (err) {
      setError(err.message || 'Erreur lors de la création');
    }
    setLoading(false);
  };

  return (
    <div className="login-page">
      <motion.div className="login-card" ref={formRef}
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="login-logo">🌳</div>
        <div className="login-title">Curiosaurus</div>
        <div className="login-subtitle">Nourris ta curiosité 🌱</div>

        <div className="login-tabs">
          <button className={`login-tab ${mode === 'login' ? 'active' : ''}`}
            onClick={() => { setMode('login'); setError(''); }}>Connexion</button>
          <button className={`login-tab ${mode === 'register' ? 'active' : ''}`}
            onClick={() => { setMode('register'); setError(''); }}>Nouveau compte</button>
        </div>

        <AnimatePresence mode="wait">
          {mode === 'login' ? (
            <motion.form key="login" onSubmit={handleLogin}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
              <div className="form-group">
                <label>Pseudo</label>
                <input type="text" value={username} onChange={e => setUsername(e.target.value)}
                  placeholder="Ton pseudo" required autoCapitalize="none" autoCorrect="off" />
              </div>
              <div className="form-group">
                <label>Code PIN (6 chiffres)</label>
                <input style={{ WebkitTextSecurity: 'disc', letterSpacing: '0.35em' }} type="tel" inputMode="numeric" pattern="[0-9]*" maxLength={6}
                  value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000" required />
              </div>
              {error && <div className="form-error">{error}</div>}
              <button className="btn-login" type="submit" disabled={loading || pin.length < 6}>
                {loading ? '⏳ Connexion...' : "🚀 C'est parti !"}
              </button>
            </motion.form>
          ) : (
            <motion.form key="register" onSubmit={handleRegister}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
              <div className="form-group">
                <label>Pseudo</label>
                <input type="text" value={regUsername} onChange={e => setRegUsername(e.target.value)}
                  placeholder="Choisis un pseudo" required autoCapitalize="none" autoCorrect="off" />
              </div>
              <div className="form-group">
                <label>Code PIN (6 chiffres)</label>
                <input style={{ WebkitTextSecurity: 'disc', letterSpacing: '0.35em' }} type="tel" inputMode="numeric" pattern="[0-9]*" maxLength={6}
                  value={regPin} onChange={e => setRegPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000" required />
              </div>
              <div className="form-group">
                <label>Confirme le PIN</label>
                <input style={{ WebkitTextSecurity: 'disc', letterSpacing: '0.35em' }} type="tel" inputMode="numeric" pattern="[0-9]*" maxLength={6}
                  value={regPinConfirm} onChange={e => setRegPinConfirm(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000" required />
              </div>
              {error && <div className="form-error">{error}</div>}
              <button className="btn-login" type="submit" disabled={loading || regPin.length < 6}>
                {loading ? '⏳ Création...' : '🌱 Créer mon compte'}
              </button>
            </motion.form>
          )}
        </AnimatePresence>

        <div className="version-badge">v{APP_VERSION}</div>
      </motion.div>
    </div>
  );
}
